import { Chip, CircularProgress, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import matchingRoundsAPI, {
  AdvancedMatchPage,
  MatchRecommendation,
} from '../../../api/matching-rounds';
import { MentorId } from '../../../api/mentors';
import { VentureId } from '../../../api/ventures/types/Venture';
import { COLORS } from '../../../theme/variables';
import { useResourceBundles } from '../../../contexts/resource-bundles-context';
import { isMobile } from '../../../utils/functions';
import {
  AssignmentRecommendedTable,
  Button,
  FormButtons,
  FormGroup,
  TextField,
} from '../../common';
import ModalV2 from '../../common/modal-v2';

/** Minimal venture info sufficient for selection and submission */
interface SelectedVenture {
  id: VentureId;
  ventureName: string;
}

interface HeatmapAssignVentureModalProps {
  open: boolean;
  onClose: () => void;
  mentorId: MentorId;
  mentorName: string;
  existingVentureIds: VentureId[];
  roundVentures: AdvancedMatchPage[];
  ventureMatchMap: Map<string, string>;
  onRecommendationCreated: (
    ventureId: VentureId,
    rec: MatchRecommendation,
  ) => void;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 285,
    height: '100%',
  },
  formButtons: {
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row wrap',
    margin: '15px 0 0 -10px',
  },
  chipItem: {
    margin: '0 0 10px 10px',
    overflow: 'hidden',
    maxWidth: '100%',

    [theme.breakpoints.up('sm')]: {
      overflow: 'initial',
    },
  },
  infoLabel: {
    fontSize: 13,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    margin: '-16px 0 26px',
    lineHeight: 1.4,
  },
  emptyLabel: {
    fontSize: 14,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    textAlign: 'center' as const,
    marginTop: 24,
  },
}));

function HeatmapAssignVentureModal({
  open,
  onClose,
  mentorId,
  mentorName,
  existingVentureIds,
  roundVentures,
  ventureMatchMap,
  onRecommendationCreated,
}: HeatmapAssignVentureModalProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedVentures, setSelectedVentures] = useState<SelectedVenture[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedVentures([]);
      setSearchText('');
    }
  }, [open]);

  const handleVentureAdd = useCallback((venture: SelectedVenture) => {
    setSelectedVentures((prev) => [...prev, venture]);
  }, []);

  const handleVentureRemove = useCallback((venture: SelectedVenture) => {
    setSelectedVentures((prev) => prev.filter((v) => v.id !== venture.id));
  }, []);

  const handleApply = useCallback(async () => {
    if (selectedVentures.length === 0) return;
    setIsSubmitting(true);

    let successCount = 0;

    for (const venture of selectedVentures) {
      const matchId = ventureMatchMap.get(venture.id);
      if (!matchId) continue;

      try {
        const rec = await matchingRoundsAPI.createRecommendation(
          matchId,
          { mentorId },
        );
        onRecommendationCreated(venture.id, rec);
        successCount++;
      } catch (e: unknown) {
        const msg =
          (e as AxiosError)?.response?.data?.message ||
          'Failed to create recommendation';
        enqueueSnackbar(`Failed to assign ${venture.ventureName}`, {
          variant: 'error',
        });
        console.error('Failed to create recommendation:', msg);
      }
    }

    setIsSubmitting(false);
    if (successCount > 0) {
      onClose();
    }
  }, [
    selectedVentures,
    ventureMatchMap,
    mentorId,
    onRecommendationCreated,
    onClose,
    enqueueSnackbar,
  ]);

  // Filter round ventures: exclude already assigned/selected, apply search text
  const filteredVentures = useMemo(() => {
    const excludedIds = new Set([
      ...existingVentureIds,
      ...selectedVentures.map((v) => v.id),
    ]);
    const searchLower = searchText.toLowerCase().trim();
    return roundVentures
      .filter((v) => !excludedIds.has(v.ventureId))
      .filter(
        (v) =>
          !searchLower || v.ventureName.toLowerCase().includes(searchLower),
      )
      .map((v) => ({
        id: v.ventureId,
        ventureName: v.ventureName,
        assignedCount: v.recommendations.length,
      }));
  }, [roundVentures, existingVentureIds, selectedVentures, searchText]);

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      width='720px'
      titleProps={{
        title: 'New assignment',
        caption: mentorName,
      }}
    >
      <div className={classes.form} data-testid='heatmap-assign-venture-form'>
        <p className={classes.infoLabel}>
          You can only select Ventures that are a part of the current Matching
          Round. If you need to add Ventures to the round, please modify the
          Subgroup list.
        </p>
        <FormGroup>
          <TextField
            value={searchText}
            label='Assign to'
            placeholder='Enter venture name'
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </FormGroup>
        <div className={classes.chips}>
          {selectedVentures.map((venture) => (
            <Chip
              key={venture.id}
              label={venture.ventureName}
              onDelete={() => handleVentureRemove(venture)}
              className={classes.chipItem}
              data-testid={`selected-venture-${venture.id}`}
            />
          ))}
        </div>
        {filteredVentures.length === 0 && selectedVentures.length === 0 && (
          <p className={classes.emptyLabel}>
            We were not able to find any Ventures you can assign
          </p>
        )}
        {filteredVentures.length > 0 && (
          <AssignmentRecommendedTable
            labels={[
              'Ventures',
              isMobile() ? 'Assigned' : `Assigned ${rb('mentors-u')}`,
            ]}
            rows={filteredVentures.map((v) => ({
              name: v.ventureName,
              assigned: v.assignedCount,
              onAdd: () =>
                handleVentureAdd({
                  id: v.id,
                  ventureName: v.ventureName,
                }),
            }))}
            mobileFullHeight
          />
        )}
        <FormButtons className={classes.formButtons}>
          <Button
            onClick={onClose}
            variant='outlined'
            data-testid='heatmap-assign-venture-cancel'
          >
            Cancel
          </Button>
          <Button
            disabled={selectedVentures.length === 0 || isSubmitting}
            onClick={handleApply}
            data-testid='heatmap-assign-venture-apply'
          >
            {isSubmitting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Apply'
            )}
          </Button>
        </FormButtons>
      </div>
    </ModalV2>
  );
}

export default HeatmapAssignVentureModal;
